<template>
  <div class="sac-leaderboard">
    <div class="sac-leaderboard__inner">
      <header class="sac-leaderboard__header text-center">
        <h1 class="text-h4 font-weight-bold">Mejores resultados — Autodiagnóstico SAC</h1>
        <p class="sac-leaderboard__subtitle">
          Estos son los socios ASOFOM que decidieron compartir públicamente su resultado.
        </p>
      </header>

      <div v-if="loading" class="pa-8 text-center">
        <v-progress-circular indeterminate color="white" />
      </div>
      <v-alert v-else-if="error" type="error" class="mb-4">{{ error }}</v-alert>
      <v-alert v-else-if="!items.length" type="info" class="mb-4">
        Aún no hay resultados publicados. Sé el primero en compartir el tuyo.
      </v-alert>

      <div v-else>
        <section v-if="items.length >= 1" class="sac-leaderboard__podium">
          <div
            v-for="(slot, idx) in podiumSlots"
            :key="typeof slot === 'number' ? `slot-${slot}` : `placeholder-${idx}`"
            class="sac-leaderboard__podium-slot"
            :class="[
              typeof slot === 'number' ? `sac-leaderboard__podium-slot--${slot + 1}` : 'sac-leaderboard__podium-slot--placeholder',
            ]"
          >
            <template v-if="typeof slot === 'number' && items[slot]">
              <div class="sac-leaderboard__podium-crown">
                <v-icon v-if="slot === 0" color="amber" size="32">mdi-crown</v-icon>
              </div>
              <v-avatar
                :color="slot === 0 ? 'amber' : 'navy-light'"
                :size="slot === 0 ? 80 : 64"
                class="white--text sac-leaderboard__podium-avatar"
              >
                <span class="font-weight-bold">#{{ slot + 1 }}</span>
              </v-avatar>
              <div class="sac-leaderboard__podium-name">
                {{ items[slot].memberName }}
              </div>
              <div v-if="items[slot].company" class="sac-leaderboard__podium-company">
                {{ items[slot].company }}
              </div>
              <div class="sac-leaderboard__podium-bar">
                <span class="sac-leaderboard__podium-score">
                  {{ Math.round(items[slot].globalScore || 0) }}/100
                </span>
              </div>
            </template>
            <div v-else class="sac-leaderboard__podium-empty">
              <v-icon size="40" color="rgba(255,181,0,0.4)">mdi-trophy-outline</v-icon>
              <span>—</span>
            </div>
          </div>
        </section>

        <v-card v-if="items.length" class="sac-leaderboard__card pa-4 mt-6" outlined>
          <h3 class="text-h6 mb-3">Mejores resultados</h3>
          <v-list two-line class="sac-leaderboard__list">
            <template v-for="(item, idx) in items">
              <v-list-item :key="`row-${item.responseId}`">
                <v-list-item-avatar>
                  <v-avatar :color="colorForLevel(item.globalLevel)" size="40" class="white--text">
                    <span class="font-weight-bold">{{ idx + 1 }}</span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>
                    <span class="font-weight-medium">{{ item.memberName }}</span>
                    <v-chip
                      :color="colorForLevel(item.globalLevel)"
                      text-color="white"
                      x-small
                      class="ml-2"
                    >{{ item.globalLevel || '—' }}</v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="item.company">{{ item.company }} · </span>
                    {{ Math.round(item.globalScore || 0) }}/100 ·
                    completado el {{ formatDate(item.completedAt) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="item.rubros && item.rubros.length">
                  <v-tooltip location="top" max-width="320">
                    <template #activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on" @click="toggleRubroPanel(item.responseId)">
                        <v-icon :color="openRubroId === item.responseId ? 'amber' : ''">mdi-chart-radar</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver desglose por rubro</span>
                  </v-tooltip>
                </v-list-item-action>
              </v-list-item>

              <v-expand-transition :key="`expand-${item.responseId}`">
                <div
                  v-if="openRubroId === item.responseId && item.rubros && item.rubros.length"
                  class="px-4 pb-4"
                >
                  <SacRadarChart :data="chartFor(item)" :force-max="100" height="280px" />
                </div>
              </v-expand-transition>

              <v-divider
                v-if="idx < items.length - 1"
                :key="`div-${item.responseId}`"
              />
            </template>
          </v-list>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import { getLeaderboard } from '../../api/sac';
import SacRadarChart from '../../components/sac/SacRadarChart.vue';

export default {
    name: 'SacLeaderboard',
    components: { SacRadarChart },
    data() {
        return {
            loading: true,
            error: null,
            items: [],
            openRubroId: null,
        };
    },
    computed: {
        topItem() {
            return this.items[0];
        },
        podiumSlots() {
            const n = this.items.length;
            if (n >= 3) return [1, 0, 2];
            if (n === 2) return [1, 0, 'placeholder-3'];
            if (n === 1) return ['placeholder-2', 0, 'placeholder-3'];
            return [];
        },
    },
    async mounted() {
        try {
            const data = await getLeaderboard({ limit: 100, detail: 'rubros' });
            this.items = (data && data.items) || [];
        } catch (e) {
            this.error = (e && e.response && e.response.data && (e.response.data.error && e.response.data.error.message || e.response.data.message))
                || (e && e.message)
                || 'No se pudo cargar el ranking';
        } finally {
            this.loading = false;
        }
    },
    methods: {
        colorForLevel(level) {
            return {
                Crítico: 'red',
                Básico: 'orange',
                Aceptable: 'amber',
                Avanzado: 'green',
                Excelente: 'blue',
            }[level] || 'grey';
        },
        formatDate(d) {
            return d ? new Date(d).toLocaleDateString('es-MX') : '';
        },
        toggleRubroPanel(id) {
            this.openRubroId = this.openRubroId === id ? null : id;
        },
        chartFor(item) {
            const labels = (item.rubros || []).map((r) => r.name);
            const data = (item.rubros || []).map((r) => Math.round(r.pct || 0));
            return {
                labels,
                datasets: [
                    {
                        label: 'Porcentaje por rubro',
                        data,
                        backgroundColor: 'rgba(255, 181, 0, 0.20)',
                        borderColor: '#FFB500',
                        borderWidth: 2,
                    },
                ],
            };
        },
    },
};
</script>

<style scoped>
.sac-leaderboard {
  position: relative;
  width: 100%;
  min-height: 100vh;
  background: var(--gradient-chamber);
  color: var(--color-navy-on);
  padding: var(--space-12) var(--gutter-desktop);
  box-sizing: border-box;
}

.sac-leaderboard::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
      circle at 20% 0%,
      rgba(255, 181, 0, 0.08) 0%,
      transparent 40%
    ),
    radial-gradient(
      circle at 100% 100%,
      rgba(36, 178, 218, 0.10) 0%,
      transparent 50%
    );
  pointer-events: none;
}

.sac-leaderboard__inner {
  position: relative;
  z-index: 1;
  max-width: 980px;
  margin: 0 auto;
}

.sac-leaderboard__header {
  margin-bottom: var(--space-8);
}

.sac-leaderboard__header h1 {
  color: var(--color-navy-on);
  font-family: var(--font-headline);
  letter-spacing: var(--tracking-tight);
  margin-bottom: var(--space-2);
}

.sac-leaderboard__subtitle {
  color: var(--color-navy-on-soft);
  font-family: var(--font-body);
  font-size: var(--fs-body-md);
  line-height: var(--lh-normal);
  margin: 0;
}

/* Tarjetas: fondo navy-light del design system, sin blanco de Vuetify */
.sac-leaderboard__card {
  background-color: var(--color-navy-light) !important;
  color: var(--color-navy-on) !important;
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-amber);
  box-shadow: var(--shadow-card);
}

.sac-leaderboard__card .v-list,
.sac-leaderboard__card .v-list-item,
.sac-leaderboard__card .v-divider {
  background: transparent !important;
  border-color: rgba(255, 181, 0, 0.25) !important;
}

.sac-leaderboard__card h3 {
  color: var(--color-navy-on);
  font-family: var(--font-headline);
}

.sac-leaderboard__card .text-caption,
.sac-leaderboard__card .grey--text,
.sac-leaderboard__card .v-list-item__subtitle {
  color: var(--color-navy-on-soft) !important;
  caret-color: var(--color-navy-on-soft);
}

.sac-leaderboard__card .v-list-item__title,
.sac-leaderboard__card .text-h6 {
  color: var(--color-navy-on) !important;
}

.sac-leaderboard__card .v-icon,
.sac-leaderboard__card .v-btn .v-icon {
  color: var(--color-amber);
}

/* Podio top 3 — look diferenciado: tarjetas elevadas, sin borde plano, con glow */
.sac-leaderboard__podium {
  display: grid;
  grid-template-columns: 1fr 1.15fr 1fr;
  gap: var(--space-6);
  align-items: end;
  margin-bottom: var(--space-10);
  padding: var(--space-8) var(--space-4);
  position: relative;
}

.sac-leaderboard__podium::before {
  content: "";
  position: absolute;
  inset: auto 10% var(--space-4) 10%;
  height: 4px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 181, 0, 0.4) 50%,
    transparent 100%
  );
  border-radius: var(--radius-full);
}

.sac-leaderboard__podium-slot {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--space-6) var(--space-4) var(--space-4);
  border-radius: var(--radius-2xl);
  background: linear-gradient(
    180deg,
    rgba(36, 178, 218, 0.08) 0%,
    rgba(27, 39, 62, 0.4) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
}

.sac-leaderboard__podium-slot--1 {
  padding-top: var(--space-8);
  min-height: 380px;
  justify-content: space-between;
  background: linear-gradient(
    180deg,
    rgba(255, 181, 0, 0.18) 0%,
    rgba(27, 39, 62, 0.5) 100%
  );
  border-color: rgba(255, 181, 0, 0.45);
  box-shadow: var(--shadow-amber);
}

.sac-leaderboard__podium-slot--1::after {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 3px;
  background: var(--gradient-amber);
  border-radius: 0 0 var(--radius-full) var(--radius-full);
}

.sac-leaderboard__podium-slot--2,
.sac-leaderboard__podium-slot--3 {
  min-height: 320px;
  justify-content: space-between;
  opacity: 0.96;
}

.sac-leaderboard__podium-slot--placeholder {
  min-height: 280px;
  justify-content: center;
  opacity: 0.4;
  background: transparent;
  border-style: dashed;
  border-color: rgba(255, 181, 0, 0.25);
  box-shadow: none;
}

.sac-leaderboard__podium-crown {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sac-leaderboard__podium-avatar {
  border: 2px solid var(--color-amber);
  box-shadow: 0 0 0 4px rgba(255, 181, 0, 0.15);
}

.sac-leaderboard__podium-name {
  margin-top: var(--space-3);
  font-family: var(--font-headline);
  font-weight: var(--fw-semibold);
  color: var(--color-navy-on);
  font-size: var(--fs-body-md);
  line-height: var(--lh-snug);
  word-break: break-word;
}

.sac-leaderboard__podium-slot--1 .sac-leaderboard__podium-name {
  font-size: var(--fs-body-lg);
  color: var(--color-amber);
}

.sac-leaderboard__podium-company {
  color: var(--color-navy-on-soft);
  font-size: var(--fs-caption);
  margin-bottom: var(--space-3);
  word-break: break-word;
}

.sac-leaderboard__podium-bar {
  margin-top: var(--space-4);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
}

.sac-leaderboard__podium-slot--1 .sac-leaderboard__podium-bar {
  background: var(--gradient-amber);
  border: none;
  color: var(--color-amber-on);
}

.sac-leaderboard__podium-score {
  font-family: var(--font-headline);
  font-weight: var(--fw-bold);
  color: var(--color-navy-on);
  font-size: var(--fs-body-md);
}

.sac-leaderboard__podium-slot--1 .sac-leaderboard__podium-score {
  color: var(--color-amber-on);
}

.sac-leaderboard__podium-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-navy-on-soft);
  font-size: var(--fs-caption);
}

@media (max-width: 767.98px) {
  .sac-leaderboard__podium {
    grid-template-columns: 1fr;
    gap: var(--space-3);
    padding: var(--space-4);
  }
  .sac-leaderboard__podium-slot--1,
  .sac-leaderboard__podium-slot--2,
  .sac-leaderboard__podium-slot--3,
  .sac-leaderboard__podium-slot--placeholder {
    min-height: 0;
  }
  .sac-leaderboard__podium::before {
    display: none;
  }
}

@media (max-width: 1023.98px) {
  .sac-leaderboard {
    padding: var(--space-8) var(--gutter-mobile);
  }
}
</style>
